import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, Save, FolderTree } from "lucide-react";
import { api, errDetail } from "../../lib/api";
import { useCommodityGroups } from "../../lib/commodity";

let _tmp = 0;
const tmpId = () => `new-${Date.now()}-${_tmp++}`;

// Immutable tree helpers keyed by node id.
const mapTree = (nodes, fn) =>
  nodes.map((n) => { const m = fn(n); return { ...m, children: mapTree(m.children || [], fn) }; });
const removeNode = (nodes, id) =>
  nodes.filter((n) => n.id !== id).map((n) => ({ ...n, children: removeNode(n.children || [], id) }));

function reorder(nodes, id, dir) {
  const i = nodes.findIndex((n) => n.id === id);
  if (i !== -1) {
    const j = i + dir;
    if (j < 0 || j >= nodes.length) return nodes;
    const copy = [...nodes];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  }
  return nodes.map((n) => ({ ...n, children: reorder(n.children || [], id, dir) }));
}
// Indent: make node a child of its previous sibling.
function indent(nodes, id) {
  const i = nodes.findIndex((n) => n.id === id);
  if (i > 0) {
    const copy = [...nodes];
    const [node] = copy.splice(i, 1);
    copy[i - 1] = { ...copy[i - 1], children: [...(copy[i - 1].children || []), node] };
    return copy;
  }
  if (i === 0) return nodes;
  return nodes.map((n) => ({ ...n, children: indent(n.children || [], id) }));
}
// Flatten to rows for non-recursive rendering.
function flatRows(nodes, level = 0, acc = []) {
  for (const n of nodes) { acc.push({ node: n, level }); flatRows(n.children || [], level + 1, acc); }
  return acc;
}
function flatFind(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = flatFind(n.children || [], id);
    if (f) return f;
  }
  return null;
}
function stripTmp(nodes) {
  return nodes.map((n) => ({
    id: String(n.id).startsWith("new-") ? undefined : n.id,
    code: n.code || "", name: n.name || "", children: stripTmp(n.children || []),
  }));
}

export function CommodityGroupTreeEditor() {
  const { tree, reload } = useCommodityGroups();
  const [nodes, setNodes] = useState(null);
  const [saving, setSaving] = useState(false);
  const working = nodes ?? tree;
  const rows = flatRows(working);

  const patch = (id, key, val) =>
    setNodes(mapTree(working, (n) => (n.id === id ? { ...n, [key]: val } : n)));
  const del = (id) => {
    const node = flatFind(working, id);
    if ((node?.children || []).length && !window.confirm("Diese Warengruppe hat Unterknoten. Wirklich mit allen Unterknoten löschen?")) return;
    setNodes(removeNode(working, id));
  };
  const addChild = (parentId) =>
    setNodes(mapTree(working, (n) => (n.id === parentId
      ? { ...n, children: [...(n.children || []), { id: tmpId(), code: "", name: "Neue Warengruppe", children: [] }] }
      : n)));
  const addRoot = () =>
    setNodes([...(working), { id: tmpId(), code: "", name: "Neue Hauptgruppe", children: [] }]);
  const move = (id, dir) => setNodes(reorder(working, id, dir));
  const doIndent = (id) => setNodes(indent(working, id));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/commodity-groups", { tree: stripTmp(working) });
      toast.success("Warengruppen gespeichert ✓");
      setNodes(null);
      await reload();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="commodity-groups-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderTree className="h-5 w-5 text-[#1B2A4A]" />
          <div>
            <h3 className="font-semibold text-[#1B2A4A]">Warengruppen</h3>
            <p className="text-xs text-gray-500">Hierarchische Materialgruppen — für Lieferanten und eigene Produkte.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addRoot} data-testid="cg-add-root-btn">
            <Plus className="h-4 w-4 mr-1" />Hauptgruppe
          </Button>
          <Button size="sm" className="bg-[#1B2A4A] text-white" onClick={save} disabled={saving || nodes === null}
            data-testid="cg-save-btn">
            <Save className="h-4 w-4 mr-1" />{saving ? "Speichert…" : "Speichern"}
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-2">
        {rows.length === 0 && <div className="p-4 text-sm text-gray-400">Noch keine Warengruppen. „Hauptgruppe" hinzufügen.</div>}
        {rows.map(({ node, level }) => (
          <div key={node.id} className="flex items-center gap-2 py-1" style={{ paddingLeft: level * 20 }}
            data-testid={`cg-edit-node-${node.id}`}>
            <Input className="h-8 w-28 font-mono text-xs" placeholder="Code" value={node.code || ""}
              onChange={(e) => patch(node.id, "code", e.target.value)} data-testid={`cg-code-${node.id}`} />
            <Input className="h-8 flex-1" placeholder="Name" value={node.name || ""}
              onChange={(e) => patch(node.id, "name", e.target.value)} data-testid={`cg-name-${node.id}`} />
            <button className="p-1 text-gray-400 hover:text-[#1B2A4A]" onClick={() => move(node.id, -1)} title="Nach oben"><ChevronUp className="h-4 w-4" /></button>
            <button className="p-1 text-gray-400 hover:text-[#1B2A4A]" onClick={() => move(node.id, 1)} title="Nach unten"><ChevronDown className="h-4 w-4" /></button>
            <button className="p-1 text-gray-400 hover:text-[#1B2A4A]" onClick={() => doIndent(node.id)} title="Einrücken (unter vorherige Gruppe)"><ChevronRight className="h-4 w-4" /></button>
            <button className="p-1 text-gray-400 hover:text-[#1B2A4A]" onClick={() => addChild(node.id)} title="Untergruppe hinzufügen" data-testid={`cg-add-child-${node.id}`}><Plus className="h-4 w-4" /></button>
            <button className="p-1 text-gray-400 hover:text-red-500" onClick={() => del(node.id)} title="Löschen" data-testid={`cg-del-${node.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
